/**
 * Kennzahlen-Glossar: Formel und Einschränkung je Kachel.
 *
 * Anlass (Armin Kern, Bereichsleiter, 08.09.2026): „Bei KPIs fände ich gut,
 * wenn irgendwo eine Beschreibung sichtbar ist, wie sie sich zusammensetzt —
 * z. B. bei ‚Mein Team‘ die Auslastungs-KPI: hier ist mir nicht klar, ob / wie
 * Abwesenheiten & Teilzeit berücksichtigt werden?"
 *
 * Es GAB diese Texte schon — aber nur im Detailfenster einer Person, geliefert
 * vom Backend. Auf „Mein Team" und in der Wirtschaftlichkeits-Übersicht fehlten
 * sie. Statt eine zweite Liste anzulegen, steht das Glossar jetzt hier an EINER
 * Stelle und gilt für alle Sichten; die Abschnitte unten sind nach Sicht
 * gegliedert, darunter „In der Probezeit". Die Backend-Fassung ist entfallen.
 *
 * ⚠️ Diese Texte sind ein Versprechen an die Führungskraft. Wer eine Formel im
 * Code ändert, ändert sie hier mit — zwei der Einträge waren am 09.09.2026
 * veraltet (Festpreis-Zuordnung und die längst entfernte Kranktage-Ampel) und
 * haben Nutzern das Falsche erzählt.
 */

export interface GlossarEintrag {
  formel: string;
  hinweis: string;
}

/**
 * Regeln der Wirtschafts-Ampel, einmal formuliert und an mehreren Kennzahlen
 * gezeigt. Die Zahlen stehen in config/wirtschaftlichkeit.yaml — wer dort eine
 * Schwelle ändert, ändert sie hier mit.
 */
const AMPEL_REGELN = [
  "• Auslastung Ø 3M: unter 70 % gelb, unter 60 % rot",
  "• Marge Ø 3M: unter 20 % gelb, unter 0 % oder kein Umsatz rot",
  "• Stundensatz: unter dem Einkaufssatz rot, weniger als 20 % darüber gelb",
  "• Umsatz oder Stunden gegenüber den 3 Monaten davor: 15 % weniger gelb, 30 % weniger rot",
  "• Keine fakturierten Stunden im Vormonat: gelb",
].join("\n");

export const GLOSSAR: Record<string, GlossarEintrag> = {
  // ── Detailfenster einer Person ──────────────────────────────────────────
  deckungsbeitragPct3m: {
    formel: "Umsatz Ø3M − Kosten Ø3M · Marge = davon ÷ Umsatz Ø3M",
    hinweis:
      "Links der Betrag, rechts die Marge — wie viel Prozent des Umsatzes als Deckungsbeitrag übrig bleiben. Bezug der Marge ist der Umsatz, nicht die Kosten. Ohne Umsatz zeigt die Kachel „—“ — die Ampel meldet das als kritisch.",
  },
  deckungsbeitrag12m: {
    formel: "Umsatz 12M − Vollkosten 12M · Marge = davon ÷ Umsatz 12M",
    hinweis:
      "Die Zahl, die „trägt sich diese Person?“ beantwortet. Links der Betrag, rechts die Marge. Kosten mit Teilzeitfaktor und erst ab Eintritt.",
  },
  kosten12m: {
    formel: "Σ Rollen-Vollkosten je Monat, letzte 12 abgeschlossene Monate",
    hinweis:
      "Mit Teilzeitfaktor, erst ab Eintrittsmonat, und mit der Karrierestufe DES JEWEILIGEN MONATS — eine Beförderung verteuert die Vergangenheit nicht rückwirkend. Krankheit senkt die Kosten NICHT. Unbekannte Rolle zählt 0, dann ist der Deckungsbeitrag zu günstig.",
  },
  utilizationAvg12m: {
    formel: "fakturierte ÷ verfügbare Stunden, letzte 12 Monate",
    hinweis:
      "Gehört zum Deckungsbeitrag — gleicher Zeitraum. Weicht sie stark von der Ø3M-Zahl ab, war das Jahr anders als das Quartal.",
  },
  umsatz12m: {
    formel: "Σ VK-Umsatz + bewertete Festpreis-Arbeit, 12 abgeschlossene Monate",
    hinweis:
      "Erwarteter Umsatz aus den Kontierungen (die fakturierte Rechnungssumme ist in Salesforce nicht gepflegt). Stunden auf Festpreisprojekten tragen keinen VK-Umsatz und werden mit dem Einkaufspreis aus Salesforce bewertet. Der Vertragsumsatz des Projekts zählt bei niemandem — ob das PROJEKT profitabel war, gehört ins DB-Dashboard.",
  },
  hourlyRate3m: {
    formel: "VK-Umsatz ÷ fakturierbare Stunden (Ø 3 Monate)",
    hinweis: "Festpreis-Stunden sind ausgeklammert, damit der Satz die Marktrate zeigt.",
  },
  utilizationAvg3m: {
    formel: "fakturierte Stunden ÷ Sollfaktura (Ø 3 Monate)",
    hinweis:
      "Sollfaktura = (Wochenstunden ÷ 5) × (Arbeitstage − Feiertage − Urlaub − Krankheitsausfall − Auszeit). Teilzeit steckt in den Wochenstunden, beides monatsgenau aus Personio — ein Teilzeitwechsel wirkt nicht rückwirkend. Elternzeit, Mutterschutz, Sabbatical, Freistellung und Theoriephase zählen als Auszeit. Fortbildung und Homeoffice senken die Sollfaktura NICHT, das ist Arbeitszeit.",
  },
  monthlyCost: {
    formel: "Rollen-Vollkosten × Teilzeitfaktor (Wochenstunden ÷ 40)",
    hinweis:
      "Unbekannte Rollen zeigen „—“ statt 0. Krankheit oder Urlaub senken die Kosten nicht — sie laufen weiter. Sätze: config/wirtschaftlichkeit.yaml.",
  },
  sickDays12m: {
    formel:
      "Σ Krankheitsausfall in Arbeitstagen, 12 abgeschlossene Monate, einschließlich „Kind krank“",
    hinweis:
      "Arbeitstage, an denen der Mitarbeiter wegen Krankheit nicht arbeiten konnte, einschließlich ‚Kind krank‘, in den letzten 12 abgeschlossenen Monaten. Mindert die verfügbare Zeit für Soll-Faktura und Auslastung. Die eigenen Krankheitstage in der Bindung können deshalb abweichen: Sie zählen ‚Kind krank‘ nicht und reichen bis heute. Wird angezeigt, aber nicht bewertet.",
  },
  eigeneKrankheitstage: {
    formel:
      "Σ eigene Krankheitstage in Arbeitstagen, letzte 12 und 3 Monate bis heute · Zahl der Krankmeldungen (zusammenhängende Einträge zählen einmal)",
    hinweis:
      "Arbeitstage, an denen der Mitarbeiter selbst krank gemeldet war, in den letzten 12 Monaten bis heute. ‚Kind krank‘ zählt hier nicht, weil die Person selbst nicht krank ist. Der Krankheitsausfall in der Wirtschaftlichkeit kann deshalb abweichen: Er zählt ‚Kind krank‘ mit und nur abgeschlossene Monate.",
  },

  // ── Wirtschaftlichkeit, Übersicht und Team ──────────────────────────────
  gesamtUmsatz12m: {
    formel: "Σ Umsatz aller sichtbaren Mitarbeiter, 12 abgeschlossene Monate",
    hinweis:
      "Erwarteter Umsatz aus den Kontierungen, VK-Geschäft plus bewertete Festpreis-Arbeit. Der laufende Monat zählt nicht mit — er ist noch unvollständig kontiert.",
  },
  gesamtKosten12m: {
    formel: "Σ Rollen-Vollkosten aller sichtbaren Mitarbeiter, 12 Monate",
    hinweis:
      "Je Monat die Kosten der Karrierestufe, die damals galt, mit Teilzeitfaktor und erst ab Eintritt. Ist für eine Rolle kein Kostensatz hinterlegt, fehlt sie hier — der Wert ist dann eine Untergrenze und die Kachel weist darauf hin.",
  },
  gesamtDeckungsbeitrag: {
    formel: "Umsatz − Kosten · Marge = davon ÷ Umsatz",
    hinweis:
      "Die Marge wird aus den SUMMEN gerechnet, nicht als Mittel der Einzelwerte — sonst würde ein Ausreißer mit winzigem Umsatz und vollen Kosten den Schnitt kippen.",
  },
  gesamtAuslastung3m: {
    formel: "Mittel der Auslastung über die bewerteten Mitarbeiter (Ø 3 Monate)",
    hinweis:
      "Je Person: fakturierte Stunden ÷ Sollfaktura; Urlaub, Krankheitsausfall (einschließlich ‚Kind krank‘), Auszeiten und Teilzeit sind darin schon berücksichtigt. Mitgezählt wird nur, wessen Auslastung auch die Ampel bewertet, also Consulting ohne Ausbildungsrollen, Bereichsleiter und MTB. Wer keine Sollstunden hat (etwa durchgehend krank), zählt nicht als 0 mit, sondern gar nicht.",
  },
  gesamtMitarbeiter: {
    formel: "Anzahl aktiver Mitarbeiter in dieser Sicht",
    hinweis:
      "Nur wer sichtbar ist: Führungskräfte sehen die eigene Berichtslinie, Personalleitung und HRBP alle. Ausgetretene sind nicht enthalten.",
  },

  // ── Wirtschaftlichkeit, Tabellen Teams und Mitarbeiter ─────────────────────
  // Texte von Marie am 16.09.2026 freigegeben. Die Zahlen in der Ampel-Erklärung
  // stehen in config/wirtschaftlichkeit.yaml — wer dort eine Schwelle ändert,
  // ändert sie hier mit.
  tabelleTeamMitarbeiter: {
    formel: "Anzahl aktiver Mitarbeiter im Team (in deiner Sicht)",
    hinweis:
      "Ausgetretene zählen nicht. Mitgezählt werden auch Mitarbeiter, die wirtschaftlich nicht bewertet werden, etwa Trainees oder MTB.",
  },
  tabelleTeamUmsatz12m: {
    formel: "Σ Umsatz der Mitarbeiter im Team, letzte 12 abgeschlossene Monate",
    hinweis:
      "Erwarteter Umsatz aus den Kontierungen: Stundengeschäft plus bewertete Festpreis-Arbeit. Der laufende Monat zählt nicht mit.",
  },
  tabelleTeamKosten12m: {
    formel: "Σ Vollkosten der Mitarbeiter im Team, letzte 12 abgeschlossene Monate",
    hinweis:
      "Je Monat die Kosten der Karrierestufe, die damals galt, mit Teilzeitfaktor und erst ab Eintritt. Krankheit und Urlaub senken die Kosten nicht. Ein Warndreieck heißt: Für mindestens eine Rolle fehlt der Kostensatz, der Wert ist eine Untergrenze.",
  },
  tabelleTeamDeckungsbeitrag: {
    formel: "Umsatz 12M − Kosten 12M",
    hinweis:
      "Was das Team über seine eigenen Kosten hinaus erwirtschaftet hat. Negativ heißt: Die Kosten waren höher als der Umsatz.",
  },
  tabelleTeamAuslastung3m: {
    formel: "Mittel der Auslastung (Ø 3 Monate) über die bewerteten Mitarbeiter im Team",
    hinweis:
      "Je Person: fakturierte Stunden ÷ Sollfaktura. Urlaub, Krankheitsausfall (einschließlich ‚Kind krank‘), Auszeiten und Teilzeit sind darin schon berücksichtigt. Mitgezählt wird nur, wessen Auslastung auch die Ampel bewertet, also Consulting ohne Ausbildungsrollen, Bereichsleiter und MTB. Ist das im Team niemand, steht hier „—“.",
  },
  tabelleTeamMarge: {
    formel: "Deckungsbeitrag ÷ Umsatz, 12 Monate",
    hinweis:
      "Aus den Summen des Teams gerechnet, nicht als Mittel der Einzelwerte, damit ein Ausreißer mit winzigem Umsatz den Wert nicht kippt. Ohne Umsatz steht hier „—“.",
  },
  tabelleTeamAmpeln: {
    formel: "Anzahl Mitarbeiter je Wirtschafts-Ampel: grün · gelb · rot",
    hinweis:
      "Die Regeln stehen an der Spalte „Wirtschafts-Ampel“, wenn du ein Team aufklappst. Wer nicht bewertet wird (außerhalb von Consulting, Ausbildungsrollen, Bereichsleiter, MTB), zählt hier nicht mit.",
  },
  tabelleMitarbeiterMarge: {
    formel: "(Umsatz 12M − Kosten 12M) ÷ Umsatz 12M",
    hinweis:
      "Kosten je Monat mit der damaligen Karrierestufe, Teilzeitfaktor und erst ab Eintritt. Ohne Umsatz steht hier „—“. Achtung: Die Ampel prüft die Marge der letzten 3 Monate, diese Spalte zeigt 12 Monate.",
  },
  tabelleMitarbeiterAmpel: {
    formel: "Rot, sobald eine Regel rot ist · gelb, sobald eine gelb ist · sonst grün",
    hinweis: `${AMPEL_REGELN}\n\nNicht bewertet werden: außerhalb von Consulting, Ausbildungsrollen, Bereichsleiter, MTB. Welche Regel bei einer Person anschlägt, zeigt das Darüberfahren über ihre Ampel.`,
  },

  // ── Mein Team ───────────────────────────────────────────────────────────
  teamGroesse: {
    formel: "Anzahl aktiver Mitarbeiter in der eigenen Berichtslinie",
    hinweis: "Ausgetretene sind nicht enthalten; bestätigte Kündigungen zählen noch mit.",
  },
  // Die Kachel „Risikolage" folgt seit dem 16.09.2026 dem Schalter Bindung /
  // Wirtschaftlichkeit — vorher zählte sie die Wirtschafts-Ampel, während der
  // Text hier die Bindungs-Ampel beschrieb.
  teamRisikolageBindung: {
    formel: "Verteilung der Bindungs-Ampel über das Team",
    hinweis:
      "Sie kommt aus dem Retention-Score (Betriebszugehörigkeit, Gehaltslage, Ereignisse, Krankheitsmuster). Bestätigte Kündigungen zählen nicht mit — sie gelten nicht mehr als Risiko und sind in der Liste eigens gekennzeichnet. Mit dem Schalter „Wirtschaftlichkeit“ zeigt die Kachel die Wirtschafts-Ampel.",
  },
  teamRisikolageWirtschaft: {
    formel: "Verteilung der Wirtschafts-Ampel über das Team",
    hinweis: `${AMPEL_REGELN}\n\nWer nicht bewertet wird (außerhalb von Consulting, Ausbildungsrollen, Bereichsleiter, MTB), zählt hier nicht mit. Mit dem Schalter „Bindung“ zeigt die Kachel die Bindungs-Ampel.`,
  },
  teamAuslastung3m: {
    formel: "Mittel der Auslastung (Ø 3 Monate) über die bewerteten Mitarbeiter im Team",
    hinweis:
      "Je Person: fakturierte Stunden ÷ Sollfaktura. Sollfaktura = (Wochenstunden ÷ 5) × (Arbeitstage − Feiertage − Urlaub − Krankheitsausfall − Auszeit). Teilzeit und Abwesenheiten sind also beide berücksichtigt, monatsgenau aus Personio. Fortbildung und Homeoffice gelten als Arbeitszeit und senken sie nicht. Mitgezählt wird nur, wessen Auslastung auch die Ampel bewertet, also Consulting ohne Ausbildungsrollen, Bereichsleiter und MTB.",
  },
  teamAuffaellig: {
    formel: "Mitarbeiter mit mindestens einer Ampel auf Gelb oder Rot",
    hinweis:
      "Zählt beide Ampeln — Bindung und Wirtschaftlichkeit. Wer wirtschaftlich nicht bewertet wird (außerhalb von Consulting, Ausbildungsrollen, Bereichsleiter, MTB), zählt nur über die Bindungs-Ampel. Bestätigte Kündigungen zählen nicht als Bindungsrisiko.",
  },
  // Spalten der Tabelle (Marie 18.09.2026). Auslastung, Umsatz und Marge nutzen die
  // Einträge der Wirtschaftlichkeit (`utilizationAvg3m`, `umsatz12m`,
  // `tabelleMitarbeiterMarge`) — eine Erklärung je Kennzahl.
  teamSpalteAmpeln: {
    formel: "Zwei Ampeln je Mitarbeiter: links Bindung, rechts Wirtschaftlichkeit",
    hinweis:
      "Bindung kommt aus dem Risiko-Score: rot ab 70 %, gelb ab 45 %, sonst grün. Bei bestätigter Kündigung steht statt der Ampel „Kündigung bestätigt“. Wirtschaftlichkeit: rot, sobald eine Regel rot ist, gelb, sobald eine gelb ist, sonst grün. Bei Rot steht zusätzlich ein Warnzeichen. Wer über die Ampel fährt, sieht den Grund.",
  },
  teamSpalteRisikoScore: {
    formel:
      "25 % + 75 % × Gewicht der ausgelösten Risiko-Signale ÷ Gewicht aller auswertbaren Signale",
    hinweis:
      "Ab 45 % mittleres, ab 70 % hohes Risiko. Stabilisierende Signale zählen nicht als Risiko. Bei bestätigter Kündigung steht hier kein Wert.",
  },
  teamSpalteTopSignal: {
    formel: "das ausgelöste Risiko-Signal mit dem höchsten Gewicht",
    hinweis:
      "Stabilisierende Signale erscheinen hier nicht. Alle Signale der Person stehen im Personenfenster, Reiter Bindung.",
  },

  // ── In der Probezeit (Spezifikation 2026-09-16, R2/R3; Texte Marie 08.10.2026) ──
  probezeitWoche: {
    formel: "gezählte Wochen seit Eintritt von 26",
    hinweis:
      "Die Wochen laufen ab dem Eintrittsdatum, nicht ab Montag. Eine Woche zählt erst 7 Tage nach ihrem Ende, weil Stunden nachgebucht werden. Die Ampel startet in Woche 4.",
  },
  probezeitUmsatzGegenZiel: {
    formel: "Ist-Umsatz ÷ Umsatz-Ziel der gezählten Wochen",
    hinweis:
      "Ist-Umsatz sind gebuchte Stunden zum vereinbarten Preis; Stunden auf Festpreisprojekten zählen zum Einkaufspreis. Das Umsatz-Ziel ist das Anlaufziel der Stufe mal dem Ø VK-Ziel, bei Teilzeit anteilig. Ab Woche 4 bewertet: ab 90 % grün, ab 70 % gelb, darunter rot. Rot heißt begründungspflichtig, nicht Trennung.",
  },
  probezeitStunden: {
    formel: "Σ gebuchte Stunden der gezählten Wochen",
    hinweis:
      "Alle gebuchten Stunden, auch auf Festpreisprojekten. Das Ziel ist das Anlaufziel der Stufe, bei Teilzeit anteilig.",
  },
  probezeitVk: {
    formel: "Umsatz ohne Festpreis ÷ Stunden ohne Festpreis",
    hinweis:
      "Stunden auf Festpreisprojekten zählen hier nicht mit, damit sie den Preis nicht verwässern. Deshalb ist der Umsatz nicht gleich Stunden mal Ø VK, sobald jemand auf einem Festpreisprojekt bucht. Ohne solche Stunden steht „–“.",
  },
  probezeitVollkosten: {
    formel: "Vollkosten je Woche der Stufe × gezählte Wochen, Teilzeit anteilig",
    hinweis:
      "Der Deckungsbeitrag ist Ist-Umsatz minus Vollkosten. Am Anfang ist er planmäßig negativ; bis zum Ende der Probezeit soll er mindestens ±0 erreichen.",
  },
  probezeitAmpel: {
    formel: "Ampel aus Ist-Umsatz ÷ Umsatz-Ziel, ab Woche 4",
    hinweis:
      "Ab 90 % grün „auf Kurs“, ab 70 % gelb „beobachten“, darunter rot „begründungspflichtig“. In den Wochen 1 bis 3 steht „noch keine Ampel“, weil das Minus am Anfang eingeplant ist. Nicht gerechnet werden MTB, Stufen ohne Anlaufziel und Mitarbeiter außerhalb des Kundensegments.",
  },
  probezeitEnde: {
    formel: "Eintritt plus Probezeit aus Personio",
    hinweis:
      "Die letzten 30 Tage sind hervorgehoben. Nach dem Ende verschwindet die Person aus der Liste, dann gelten die üblichen Wirtschaftlichkeitszahlen.",
  },
  probezeitKrankheitstage: {
    formel: "Σ eigene Krankheitstage in Arbeitstagen seit Eintritt · Zahl der Krankmeldungen",
    hinweis:
      "Arbeitstage, an denen der Mitarbeiter selbst krank gemeldet war, seit seinem Eintritt. ‚Kind krank‘ zählt hier nicht. Zusammenhängende Einträge zählen als eine Krankmeldung.",
  },
  probezeitBindung: {
    formel: "Stufe aus dem Risiko-Score",
    hinweis:
      "Rot ab 70 %, gelb ab 45 %, sonst grün. Bei bestätigter Kündigung steht „Kündigung bestätigt“ statt einer Stufe.",
  },
};
